<script setup lang="ts">
import { ref, onErrorCaptured } from 'vue';
import { useFormBuilderToast } from '../../composables/useFormBuilderToast';

interface Props {
  fallbackMessage?: string;
  showDetails?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  fallbackMessage: 'Ocorreu um erro ao renderizar o componente',
  showDetails: false,
});

const { showErrorToast } = useFormBuilderToast();
const hasError = ref(false);
const errorMessage = ref('');
const errorStack = ref('');

onErrorCaptured((err: Error, instance, info) => {
  hasError.value = true;
  errorMessage.value = err.message || 'Erro desconhecido';
  errorStack.value = err.stack || '';

  console.error('ErrorBoundary capturou erro:', {
    error: err,
    componentInstance: instance,
    errorInfo: info,
  });

  showErrorToast({
    title: props.fallbackMessage,
    type: 'error',
    hideIcon: true,
  });

  return false;
});

const resetError = () => {
  hasError.value = false;
  errorMessage.value = '';
  errorStack.value = '';
};
</script>

<template>
  <div class="error-boundary">
    <div v-if="hasError" class="error-state">
      <div class="error-icon">⚠️</div>
      <h3 class="error-title">{{ fallbackMessage }}</h3>

      <p class="error-message">{{ errorMessage }}</p>

      <button
        type="button"
        class="error-reset-button"
        @click="resetError"
      >
        Tentar novamente
      </button>

      <details v-if="showDetails && errorStack" class="error-details">
        <summary class="error-details-summary">Detalhes técnicos</summary>
        <pre class="error-stack">{{ errorStack }}</pre>
      </details>
    </div>

    <slot v-else />
  </div>
</template>

<style scoped>
.error-boundary {
  width: 100%;
  height: 100%;
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  min-height: 200px;
  background-color: var(--color-white-02);
  border: 1px solid var(--color-red-16);
  border-radius: 0.5rem;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.error-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-red-16);
  margin-bottom: 8px;
  text-align: center;
}

.error-message {
  font-size: 14px;
  color: var(--color-gray-07);
  margin-bottom: 16px;
  text-align: center;
  max-width: 500px;
}

.error-reset-button {
  padding: 10px 20px;
  background-color: var(--theme-secondary);
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
}

.error-reset-button:hover {
  opacity: 0.9;
}

.error-details {
  margin-top: 24px;
  width: 100%;
  max-width: 600px;
}

.error-details-summary {
  cursor: pointer;
  font-size: 13px;
  color: var(--color-gray-07);
  font-weight: 500;
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 0.25rem;
}

.error-details-summary:hover {
  background-color: rgba(0, 0, 0, 0.08);
}

.error-stack {
  margin-top: 12px;
  padding: 12px;
  background-color: #1e1e1e;
  color: #d4d4d4;
  font-family: var(--font-base);
  font-size: 12px;
  border-radius: 0.25rem;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>
